import { Alert, Badge, Button, Input, Label } from "@abbiamo/ui";
import {
  getApiErrorCode,
  USER_HAS_NO_ACTIVE_SELLERS,
} from "@seller-hermes/features/portal/portal-errors";
import { clearActiveSellerGroupId } from "@seller-hermes/features/portal/portal-local-context";
import { PortalService } from "@seller-hermes/features/portal/portal.api";
import { Auth } from "aws-amplify";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Check, CheckCircle, Copy, Eye, EyeOff } from "lucide-react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { publishLogoutMarker } from "../app/auth-session-expiry";
import { errorCodeOf, identifyUser, track } from "../app/telemetry";

type LoginView =
  | "login"
  | "forgot-request"
  | "forgot-confirm"
  | "new-password"
  | "mfa-challenge"
  | "mfa-setup";

const isSandbox = process.env["NX_ENV"] !== "PROD";

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Login fields
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  // Forgot password fields
  const [view, setView] = React.useState<LoginView>("login");
  const [forgotEmail, setForgotEmail] = React.useState("");
  const [resetCode, setResetCode] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [resetDone, setResetDone] = React.useState(false);

  // New password (temporary password challenge) fields
  const [cachedUser, setCachedUser] = React.useState<unknown>(null);
  const [tempNewPassword, setTempNewPassword] = React.useState("");
  const [tempConfirmPassword, setTempConfirmPassword] = React.useState("");
  const [showTempNewPassword, setShowTempNewPassword] = React.useState(false);
  const [showTempConfirmPassword, setShowTempConfirmPassword] = React.useState(false);

  // MFA (Cognito SOFTWARE_TOKEN_MFA) — desafio no login + enrollment forçado
  // pra usuários com mfa_required. cachedUser é reusado (challenge/enrollment).
  const [mfaCode, setMfaCode] = React.useState("");
  const [mfaSecret, setMfaSecret] = React.useState("");
  const [mfaError, setMfaError] = React.useState<string | null>(null);
  const [keyCopied, setKeyCopied] = React.useState(false);

  const copyMfaSecret = async () => {
    try {
      await navigator.clipboard.writeText(mfaSecret);
      setKeyCopied(true);
      window.setTimeout(() => setKeyCopied(false), 2000);
    } catch {
      /* clipboard indisponível — o QR segue como caminho principal */
    }
  };

  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    track({ name: "portal_login_viewed" });
    clearActiveSellerGroupId();
    const routeError = (location.state as { errorCode?: string } | null)
      ?.errorCode;
    if (routeError === USER_HAS_NO_ACTIVE_SELLERS) {
      setError("Voce nao possui acesso a nenhuma loja ativa.");
    }
  }, [location.state]);

  // Entrada fria por produto (?product=X&return_to=Y): se já existe sessão
  // Cognito ativa (cache/SSO), pula a tela de login inteira — mesmo
  // tratamento que o fluxo normal já dá pro usuário autenticado, só que
  // preservando product/return_to na navegação pra /.
  React.useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(() => {
        track({ name: "portal_login_session_found" });
        navigate({ pathname: "/", search: location.search }, { replace: true });
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openForgot = () => {
    setForgotEmail(email);
    setResetCode("");
    setNewPassword("");
    setShowNewPassword(false);
    setResetDone(false);
    setError(null);
    setView("forgot-request");
  };

  const backToLogin = () => {
    setError(null);
    setView("login");
  };

  // ── Step 1: send reset code ──────────────────────────────────────────────
  const sendResetCode = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await Auth.forgotPassword(forgotEmail.trim());
      setResetCode("");
      setNewPassword("");
      setView("forgot-confirm");
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      const message =
        code === "UserNotConfirmedException"
          ? "Este email ainda nao foi confirmado. Verifique sua caixa de entrada."
          : code === "LimitExceededException"
            ? "Muitas tentativas. Aguarde alguns minutos antes de tentar novamente."
            : "Nao foi possivel enviar o codigo. Verifique o email informado.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Step 2: confirm new password ─────────────────────────────────────────
  const confirmReset = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await Auth.forgotPasswordSubmit(
        forgotEmail.trim(),
        resetCode.trim(),
        newPassword,
      );
      setResetDone(true);
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      const message =
        code === "CodeMismatchException"
          ? "Codigo invalido. Verifique e tente novamente."
          : code === "ExpiredCodeException"
            ? "Codigo expirado. Volte e solicite um novo codigo."
            : code === "LimitExceededException"
              ? "Muitas tentativas. Aguarde alguns minutos antes de tentar novamente."
              : code === "InvalidPasswordException"
                ? "A nova senha nao atende aos requisitos de seguranca. Use ao menos 8 caracteres com letras e numeros."
                : "Nao foi possivel redefinir a senha. Tente novamente.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Pós-autenticação: valida acessos (/me) e, se mfa_required e ainda sem
  // MFA no Cognito, força o enrollment antes de entrar. ─────────────────────
  const finishLogin = async () => {
    try {
      const me = await PortalService.getMe();
      identifyUser(me.user?.email);
      if (me.user?.mfa_required) {
        const cognitoUser = await Auth.currentAuthenticatedUser();
        const pref = await Auth.getPreferredMFA(cognitoUser, {
          bypassCache: true,
        });
        if (pref !== "SOFTWARE_TOKEN_MFA") {
          const secret = await Auth.setupTOTP(cognitoUser);
          setCachedUser(cognitoUser);
          setMfaSecret(secret);
          setMfaCode("");
          setMfaError(null);
          setError(null);
          setView("mfa-setup");
          track({ name: "portal_login_challenge", challenge: "MFA_SETUP" });
          return;
        }
      }
      track({ name: "portal_login_success" });
      navigate({ pathname: "/", search: location.search });
    } catch (validationError) {
      track({
        name: "portal_login_error",
        stage: "access_validation",
        error_code:
          getApiErrorCode(validationError) ?? errorCodeOf(validationError),
      });
      publishLogoutMarker();
      await Auth.signOut().catch(() => undefined);
      setError(
        getApiErrorCode(validationError) === USER_HAS_NO_ACTIVE_SELLERS
          ? "Voce nao possui acesso a nenhuma loja ativa."
          : "Nao foi possivel validar seus acessos. Tente novamente.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Desafio MFA (usuário já enrolado): senha ok, agora o OTP ──────────────
  const confirmMfaChallenge = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (mfaCode.length !== 6 || isSubmitting) return;
    setMfaError(null);
    setIsSubmitting(true);
    try {
      await Auth.confirmSignIn(cachedUser, mfaCode, "SOFTWARE_TOKEN_MFA");
    } catch (err) {
      track({
        name: "portal_login_error",
        stage: "mfa_challenge",
        error_code: errorCodeOf(err),
      });
      setMfaError("Codigo invalido. Tente novamente.");
      setMfaCode("");
      setIsSubmitting(false);
      return;
    }
    await finishLogin();
  };

  // ── Enrollment MFA (mfa_required e ainda sem MFA): ativa o TOTP ───────────
  const confirmMfaSetup = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (mfaCode.length !== 6 || isSubmitting) return;
    setMfaError(null);
    setIsSubmitting(true);
    try {
      const cognitoUser = cachedUser ?? (await Auth.currentAuthenticatedUser());
      await Auth.verifyTotpToken(cognitoUser, mfaCode);
      await Auth.setPreferredMFA(cognitoUser, "TOTP");
      track({ name: "portal_login_success" });
      navigate({ pathname: "/", search: location.search });
    } catch (err) {
      track({
        name: "portal_login_error",
        stage: "mfa_setup",
        error_code: errorCodeOf(err),
      });
      setMfaError("Codigo invalido. Verifique e tente novamente.");
      setMfaCode("");
      setIsSubmitting(false);
    }
  };

  // ── Login submit ─────────────────────────────────────────────────────────
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    track({ name: "portal_login_attempt" });

    let user;
    try {
      user = await Auth.signIn({
        username: email,
        password,
      });
    } catch (err) {
      track({
        name: "portal_login_error",
        stage: "signin",
        error_code: errorCodeOf(err),
      });
      setError("Nao foi possivel entrar. Confira email e senha.");
      setShowPassword(false);
      setIsSubmitting(false);
      return;
    }

    if (user.challengeName) {
      track({ name: "portal_login_challenge", challenge: user.challengeName });
    }

    if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
      setCachedUser(user);
      setTempNewPassword("");
      setTempConfirmPassword("");
      setShowTempNewPassword(false);
      setShowTempConfirmPassword(false);
      setError(null);
      setIsSubmitting(false);
      setView("new-password");
      return;
    }

    // Usuário com MFA no Cognito → desafio TOTP antes de validar acessos.
    if (
      user.challengeName === "SOFTWARE_TOKEN_MFA" ||
      user.challengeName === "SMS_MFA"
    ) {
      setCachedUser(user);
      setMfaCode("");
      setMfaError(null);
      setError(null);
      setIsSubmitting(false);
      setView("mfa-challenge");
      return;
    }

    await finishLogin();
  };

  // ── Temporary password challenge ─────────────────────────────────────────
  const submitNewPassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (tempNewPassword !== tempConfirmPassword) {
      setError("As senhas nao coincidem. Verifique e tente novamente.");
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await Auth.completeNewPassword(cachedUser, tempNewPassword);
    } catch (err: unknown) {
      track({
        name: "portal_login_error",
        stage: "new_password",
        error_code: errorCodeOf(err),
      });
      const code = (err as { code?: string }).code;
      const message =
        code === "InvalidPasswordException"
          ? "A nova senha nao atende aos requisitos de seguranca. Use ao menos 8 caracteres com letras e numeros."
          : code === "NotAuthorizedException"
            ? "Sessao expirada. Volte ao login e tente novamente."
            : "Nao foi possivel definir a nova senha. Tente novamente.";
      setError(message);
      setIsSubmitting(false);
      return;
    }
    await finishLogin();
  };

  return (
    <main className="grid min-h-screen grid-cols-1 bg-ab-bg lg:grid-cols-2">
      {/* Left brand panel — desktop only. Neutral-light surface; accents come
          only from product Badges (dark text on accent, per DS). */}
      <aside
        className="relative hidden flex-col justify-between overflow-hidden border-r border-ab border-ab-border bg-ab-bg-muted p-ab-12 lg:flex"
        data-ui="brand-panel"
        aria-hidden="true"
      >
        <div className="flex items-center gap-ab-2">
          <img
            src="/logos/group_icon.png"
            alt="Abbiamo"
            className="h-7 w-7 object-contain"
          />
          <span className="text-ab-base font-ab-medium tracking-tight text-ab-fg">Abbiamo</span>
          <span className="ml-1 rounded-full bg-eui-muted px-2 py-0.5 text-[11px] font-ab-medium text-eui-muted-fg">
            {isSandbox ? "Sandbox" : "Prod"}
          </span>
        </div>

        <div>
          <p className="inline-flex items-center gap-ab-2 rounded-full bg-eui-accent-tint px-ab-3 py-ab-1 text-ab-sm font-ab-medium text-eui-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-eui-accent" aria-hidden />
            Portal do cliente
          </p>
          <h1 className="mt-ab-4 max-w-[16ch] text-ab-4xl font-ab-semibold leading-[1.05] text-ab-fg">
            Transforme sua operação,{" "}
            <span className="bg-gradient-to-r from-ab-group-dark via-ab-pay-dark to-ab-ai-dark bg-clip-text text-transparent">
              encante seus clientes.
            </span>
          </h1>
          <p className="mt-ab-5 max-w-[44ch] text-ab-md text-ab-fg-muted">
            LOG e GO, lado a lado: do pedido à porta do cliente,{" "}
            <span className="font-ab-medium text-ab-fg">sem perder o ritmo.</span>
          </p>
          <div className="mt-ab-6 flex gap-ab-2">
            <Badge variant="group">LOG - Orquestrador</Badge>
            <Badge variant="go">GO - Frota Própria</Badge>
          </div>
        </div>

        <p className="text-ab-sm text-ab-fg-muted">
          © Abbiamo · portal.abbiamolog.com
        </p>
      </aside>

      {/* Right column — form */}
      <div className="flex items-center justify-center p-ab-6">
        <section
          className="w-full max-w-[26rem] rounded-[20px] border border-ab border-ab-border bg-ab-card p-ab-8"
          aria-labelledby="portal-login-title"
        >
          {/* Mobile-only brand */}
          <div className="mb-ab-6 flex items-center gap-ab-2 lg:hidden">
            <img
              src="/logos/group_icon.png"
              alt="Abbiamo"
              className="h-6 w-6 object-contain"
            />
            <span className="text-ab-sm font-ab-medium text-ab-fg">Abbiamo</span>
          </div>

          {view === "login" && (
            <>
              <h2
                className="text-ab-2xl font-ab-semibold text-ab-fg"
                id="portal-login-title"
              >
                Entrar na sua conta
              </h2>
              <p className="mt-ab-1 text-ab-base text-ab-fg-muted">
                Acesse sua conta com seu email e senha.
              </p>

              <form className="mt-ab-6 grid gap-ab-4" onSubmit={submit}>
                <div className="grid gap-ab-1.5">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    autoComplete="email"
                    inputMode="email"
                    type="email"
                    className="h-ab-ctl-lg"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </div>

                <div className="grid gap-ab-1.5">
                  <Label htmlFor="login-password">Senha</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      autoComplete="current-password"
                      type={showPassword ? "text" : "password"}
                      className="h-ab-ctl-lg pr-ab-10"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-1 right-1 flex w-8 items-center justify-center text-ab-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ab-ring"
                      data-ui="icon-button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={
                        showPassword ? "Ocultar senha" : "Mostrar senha"
                      }
                    >
                      {showPassword ? (
                        <EyeOff size={18} aria-hidden />
                      ) : (
                        <Eye size={18} aria-hidden />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    className="text-ab-sm text-ab-fg-muted underline-offset-4 transition-colors duration-ab-fast hover:text-ab-fg hover:underline"
                    onClick={openForgot}
                  >
                    Esqueci a senha
                  </button>
                </div>

                {error ? <Alert variant="error">{error}</Alert> : null}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  loading={isSubmitting}
                >
                  Entrar
                </Button>
              </form>
            </>
          )}

          {view === "forgot-request" && (
            <>
              <button
                type="button"
                className="-ml-ab-3 mb-ab-4 disabled:opacity-40"
                data-ui="nav-link"
                onClick={backToLogin}
                disabled={isSubmitting}
              >
                <ArrowLeft size={16} aria-hidden />
                Voltar ao login
              </button>
              <h2
                className="text-ab-2xl font-ab-semibold text-ab-fg"
                id="portal-login-title"
              >
                Redefinir senha
              </h2>
              <p className="mt-ab-1 text-ab-base text-ab-fg-muted">
                Informe o email cadastrado. Enviaremos um código de verificação.
              </p>

              <form className="mt-ab-6 grid gap-ab-4" onSubmit={sendResetCode}>
                <div className="grid gap-ab-1.5">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    autoComplete="email"
                    inputMode="email"
                    type="email"
                    className="h-ab-ctl-lg"
                    value={forgotEmail}
                    onChange={(event) => setForgotEmail(event.target.value)}
                    required
                  />
                </div>

                {error ? <Alert variant="error">{error}</Alert> : null}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  loading={isSubmitting}
                >
                  Enviar código
                </Button>
              </form>
            </>
          )}

          {view === "forgot-confirm" && !resetDone && (
            <>
              <button
                type="button"
                className="-ml-ab-3 mb-ab-4 disabled:opacity-40"
                data-ui="nav-link"
                disabled={isSubmitting}
                onClick={() => {
                  setResetCode("");
                  setNewPassword("");
                  setError(null);
                  setView("forgot-request");
                }}
              >
                <ArrowLeft size={16} aria-hidden />
                Reenviar código
              </button>
              <h2
                className="text-ab-2xl font-ab-semibold text-ab-fg"
                id="portal-login-title"
              >
                Criar nova senha
              </h2>
              <p className="mt-ab-1 text-ab-base text-ab-fg-muted">
                Insira o código recebido em{" "}
                <strong className="font-ab-medium text-ab-fg">
                  {forgotEmail}
                </strong>{" "}
                e escolha uma nova senha.
              </p>

              <form className="mt-ab-6 grid gap-ab-4" onSubmit={confirmReset}>
                <div className="grid gap-ab-1.5">
                  <Label htmlFor="reset-code">Código de verificação</Label>
                  <Input
                    id="reset-code"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    type="text"
                    className="h-ab-ctl-lg"
                    value={resetCode}
                    onChange={(event) => setResetCode(event.target.value)}
                    required
                  />
                </div>

                <div className="grid gap-ab-1.5">
                  <Label htmlFor="new-password">Nova senha</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      autoComplete="new-password"
                      type={showNewPassword ? "text" : "password"}
                      className="h-ab-ctl-lg pr-ab-10"
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-1 right-1 flex w-8 items-center justify-center text-ab-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ab-ring"
                      data-ui="icon-button"
                      onClick={() => setShowNewPassword((v) => !v)}
                      aria-label={
                        showNewPassword ? "Ocultar senha" : "Mostrar senha"
                      }
                    >
                      {showNewPassword ? (
                        <EyeOff size={18} aria-hidden />
                      ) : (
                        <Eye size={18} aria-hidden />
                      )}
                    </button>
                  </div>
                </div>

                {error ? <Alert variant="error">{error}</Alert> : null}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  loading={isSubmitting}
                  disabled={
                    isSubmitting ||
                    resetCode.trim().length === 0 ||
                    newPassword.length < 8
                  }
                >
                  Confirmar nova senha
                </Button>
              </form>
            </>
          )}

          {view === "forgot-confirm" && resetDone && (
            <div className="flex flex-col items-center gap-ab-2 py-ab-4 text-center">
              <CheckCircle
                size={32}
                className="mb-ab-2 text-eui-accent"
                aria-hidden
              />
              <h2
                className="text-ab-2xl font-ab-semibold text-ab-fg"
                id="portal-login-title"
              >
                Senha redefinida!
              </h2>
              <p className="text-ab-base text-ab-fg-muted">
                Sua senha foi atualizada com sucesso. Faça login com a nova
                senha.
              </p>
              <Button size="lg" className="mt-ab-2 w-full" onClick={backToLogin}>
                Ir para o login
              </Button>
            </div>
          )}

          {view === "new-password" && (
            <>
              <button
                type="button"
                className="-ml-ab-3 mb-ab-4 disabled:opacity-40"
                data-ui="nav-link"
                onClick={backToLogin}
                disabled={isSubmitting}
              >
                <ArrowLeft size={16} aria-hidden />
                Voltar ao login
              </button>
              <h2
                className="text-ab-2xl font-ab-semibold text-ab-fg"
                id="portal-login-title"
              >
                Crie sua nova senha
              </h2>
              <p className="mt-ab-1 text-ab-base text-ab-fg-muted">
                Seu acesso foi criado com uma senha temporária. Defina uma senha
                permanente para continuar.
              </p>

              <form className="mt-ab-6 grid gap-ab-4" onSubmit={submitNewPassword}>
                <div className="grid gap-ab-1.5">
                  <Label htmlFor="temp-new-password">Nova senha</Label>
                  <div className="relative">
                    <Input
                      id="temp-new-password"
                      autoComplete="new-password"
                      type={showTempNewPassword ? "text" : "password"}
                      className="h-ab-ctl-lg pr-ab-10"
                      value={tempNewPassword}
                      onChange={(event) => setTempNewPassword(event.target.value)}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-1 right-1 flex w-8 items-center justify-center text-ab-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ab-ring"
                      data-ui="icon-button"
                      onClick={() => setShowTempNewPassword((v) => !v)}
                      aria-label={showTempNewPassword ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showTempNewPassword ? (
                        <EyeOff size={18} aria-hidden />
                      ) : (
                        <Eye size={18} aria-hidden />
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid gap-ab-1.5">
                  <Label htmlFor="temp-confirm-password">Confirmar nova senha</Label>
                  <div className="relative">
                    <Input
                      id="temp-confirm-password"
                      autoComplete="new-password"
                      type={showTempConfirmPassword ? "text" : "password"}
                      className="h-ab-ctl-lg pr-ab-10"
                      value={tempConfirmPassword}
                      onChange={(event) => setTempConfirmPassword(event.target.value)}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-1 right-1 flex w-8 items-center justify-center text-ab-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ab-ring"
                      data-ui="icon-button"
                      onClick={() => setShowTempConfirmPassword((v) => !v)}
                      aria-label={showTempConfirmPassword ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showTempConfirmPassword ? (
                        <EyeOff size={18} aria-hidden />
                      ) : (
                        <Eye size={18} aria-hidden />
                      )}
                    </button>
                  </div>
                </div>

                {error ? <Alert variant="error">{error}</Alert> : null}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  loading={isSubmitting}
                  disabled={
                    isSubmitting ||
                    tempNewPassword.length < 8 ||
                    tempConfirmPassword.length < 8
                  }
                >
                  Definir senha e entrar
                </Button>
              </form>
            </>
          )}

          {view === "mfa-challenge" && (
            <>
              <h2
                className="text-ab-2xl font-ab-semibold text-ab-fg"
                id="portal-login-title"
              >
                Verificação em duas etapas
              </h2>
              <p className="mt-ab-1 text-ab-base text-ab-fg-muted">
                Digite o código de 6 dígitos do seu app autenticador.
              </p>

              <form
                className="mt-ab-6 grid gap-ab-4"
                onSubmit={confirmMfaChallenge}
              >
                <div className="grid gap-ab-1.5">
                  <Label htmlFor="mfa-code">Código</Label>
                  <Input
                    id="mfa-code"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    type="text"
                    className="h-ab-ctl-lg text-center tracking-[0.4em]"
                    value={mfaCode}
                    onChange={(event) =>
                      setMfaCode(
                        event.target.value.replace(/\D/g, "").slice(0, 6),
                      )
                    }
                    required
                    autoFocus
                  />
                </div>

                {mfaError ? <Alert variant="error">{mfaError}</Alert> : null}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  loading={isSubmitting}
                  disabled={isSubmitting || mfaCode.length !== 6}
                >
                  Verificar
                </Button>
              </form>

              <button
                type="button"
                className="-ml-ab-3 mt-ab-4 disabled:opacity-40"
                data-ui="nav-link"
                onClick={backToLogin}
                disabled={isSubmitting}
              >
                <ArrowLeft size={16} aria-hidden />
                Voltar ao login
              </button>
            </>
          )}

          {view === "mfa-setup" && (
            <>
              <h2
                className="text-ab-xl font-ab-semibold text-ab-fg"
                id="portal-login-title"
              >
                Ative a verificação em duas etapas
              </h2>
              <p className="mt-ab-1 text-ab-sm text-ab-fg-muted">
                Escaneie o QR no seu app autenticador e digite o código gerado.
              </p>

              {mfaSecret ? (
                <div className="mt-ab-4 flex items-center gap-ab-4">
                  <div className="shrink-0 rounded-[14px] border border-ab border-ab-border bg-white p-ab-2">
                    <QRCodeSVG
                      value={`otpauth://totp/Abbiamo:${encodeURIComponent(
                        email || "user",
                      )}?secret=${mfaSecret}&issuer=Abbiamo`}
                      size={128}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-ab-xs text-ab-fg-muted">
                      Sem câmera? Copie a chave e cole no app.
                    </p>
                    <button
                      type="button"
                      onClick={copyMfaSecret}
                      className="mt-ab-2 inline-flex h-8 items-center gap-ab-1.5 rounded-full bg-eui-bg px-ab-3 text-ab-sm font-ab-medium text-ab-fg shadow-[0_0_0_1px_rgb(var(--eui-border-strong))] transition-colors duration-ab-fast hover:bg-eui-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ab-ring"
                      aria-label="Copiar chave de configuração"
                    >
                      {keyCopied ? (
                        <Check size={16} aria-hidden />
                      ) : (
                        <Copy size={16} aria-hidden />
                      )}
                      {keyCopied ? "Chave copiada" : "Copiar chave"}
                    </button>
                  </div>
                </div>
              ) : null}

              <form className="mt-ab-4 grid gap-ab-3" onSubmit={confirmMfaSetup}>
                <div className="grid gap-ab-1.5">
                  <Label htmlFor="mfa-setup-code">Código de verificação</Label>
                  <Input
                    id="mfa-setup-code"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    type="text"
                    className="h-ab-ctl-lg text-center tracking-[0.4em]"
                    value={mfaCode}
                    onChange={(event) =>
                      setMfaCode(
                        event.target.value.replace(/\D/g, "").slice(0, 6),
                      )
                    }
                    required
                    autoFocus
                  />
                </div>

                {mfaError ? <Alert variant="error">{mfaError}</Alert> : null}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  loading={isSubmitting}
                  disabled={isSubmitting || mfaCode.length !== 6}
                >
                  Ativar e continuar
                </Button>
              </form>
            </>
          )}
        </section>
      </div>
    </main>
  );
};
